import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { CheckCircle2, XCircle, Clock, Coins, CalendarPlus, PencilLine } from "lucide-react";

/**
 * Central Approvals inbox — shows every pending kid-initiated request across
 * the family. Designed to grow beyond token requests (extra time, purchases,
 * etc.) — each request type just needs a small renderer in `describe()`.
 */
export default function Approvals() {
  const [approvals, setApprovals] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [outlook, setOutlook] = useState(false); // Outlook connected: kids' calendar items can go straight in
  const [account, setAccount] = useState(""); // the Outlook account that gets changed, for the warning

  useEffect(() => {
    api.get("/calendar/outlook").then(({ data }) => {
      setOutlook(!!(data.connected && data.calendar_id));
      setAccount(data.account || "");
    }).catch(() => {});
  }, []);

  // Anything that writes to Outlook says so first: it changes the family's real calendar.
  function toOutlook(a) {
    const p = a.payload || {};
    const where = account ? `your Outlook calendar (${account})` : "your Outlook calendar";
    const msg = a.type === "event_rename"
      ? `This renames "${p.old_title}" to "${p.new_title}" in ${where}${p.repeat ? ", on every date" : ""}. Everyone who uses that calendar will see the new name.\n\nUpdate Outlook?`
      : `This adds "${p.title}" to ${where}. Everyone who uses that calendar will see it.\n\nAdd to Outlook?`;
    if (window.confirm(msg)) resolve(a.id, "approve", { add_to_outlook: true });
  }

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get(`/approvals?status=${filter}`);
      setApprovals(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [filter]);

  // New requests show up by themselves: check every 15 seconds, and as soon as the tab is looked at again.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") load(); };
    const id = setInterval(tick, 15000);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [filter]);

  async function resolve(id, action, body = {}) {
    setBusy(id);
    try {
      await api.post(`/approvals/${id}/${action}`, body);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  }

  function describe(a) {
    if (a.type === "token_request") {
      const p = a.payload || {};
      return {
        icon: <Coins className="text-[#F2B544]" />,
        title: `${a.kid_name} is requesting ${p.count || 1} token${(p.count || 1) > 1 ? "s" : ""}`,
        reason: p.reason || "(no reason given)",
      };
    }
    if (a.type === "time_request") {
      const p = a.payload || {};
      return {
        icon: <Clock className="text-[#5C7A4F]" />,
        title: `${a.kid_name} is asking for ${p.minutes || "more"} more minutes`,
        reason: p.reason || "(no reason given)",
      };
    }
    if (a.type === "event_request") {
      const p = a.payload || {};
      const s = new Date(p.start_at);
      const day = s.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
      const t = (d) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
      const when = p.all_day ? `${day}, all day` : `${day}, ${t(s)} to ${t(new Date(p.end_at))}`;
      return {
        icon: <CalendarPlus className="text-[#3E98D8]" />,
        title: `${a.kid_name} added ${p.emoji || "📅"} ${p.title}`,
        reason: [when, p.location, p.notes].filter(Boolean).join(" · "),
        event: true,
      };
    }
    if (a.type === "event_rename") {
      const p = a.payload || {};
      const s = new Date(p.start_at);
      const day = s.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
      return {
        icon: <PencilLine className="text-[#3E98D8]" />,
        title: `${a.kid_name} renamed ${p.emoji || "📅"} ${p.old_title} to "${p.new_title}"`,
        reason: [p.repeat ? `${p.repeat} (every date)` : day, p.source === "outlook" ? "From Outlook" : "In Yoda"]
          .filter(Boolean).join(" · "),
        rename: true,
        outlookEvent: p.source === "outlook",
        first: (a.kid_name || "them").split(" ")[0],
      };
    }
    return { icon: <Clock />, title: `${a.kid_name} — ${a.type}`, reason: JSON.stringify(a.payload) };
  }

  return (
    <div className="max-w-3xl">
      <header className="mb-6">
        <h1 className="text-[32px] md:text-[38px] font-bold tracking-tight text-[#2C342A]">Requests</h1>
        <p className="text-sm text-[#707A6E]">Everything waiting on you, in one place</p>
      </header>

      <div className="mb-5 flex rounded-[13px] bg-[#E8E6DF] p-[3px]">
        {[
          { id: "pending", label: "Waiting" },
          { id: "approved", label: "Said yes" },
          { id: "denied", label: "Said no" },
          { id: "all", label: "All" },
        ].map((t) => (
          <button key={t.id}
                  data-testid={`approvals-filter-${t.id}`}
                  onClick={() => setFilter(t.id)}
                  className={`flex-1 rounded-[10px] py-2 text-sm font-semibold transition-colors ${
                    filter === t.id ? "bg-white text-[#2C342A] shadow-sm" : "text-[#707A6E] hover:text-[#2C342A]"
                  }`}>{t.label}</button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-xl bg-[#F6E3DC] p-3 text-sm text-[#A35A42]" data-testid="approvals-error">{error}</div>
      )}

      {approvals.length === 0 ? (
        <div className="card-soft p-8 text-center text-[#707A6E]" data-testid="approvals-empty">
          {filter === "pending" ? "No pending requests. 🎉" : `No ${filter} requests.`}
        </div>
      ) : (
        <div className="space-y-3">
          {approvals.map((a) => {
            const d = describe(a);
            return (
              <div key={a.id} className="card-soft p-4" data-testid={`approval-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="mt-1">{d.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[16px] font-semibold text-[#2C342A]">{d.title}</div>
                    <div className="text-sm italic text-[#707A6E] mt-1">{d.event || d.rename ? d.reason : `"${d.reason}"`}</div>
                    <div className="text-xs text-[#A6ABA3] mt-2">
                      {new Date(a.created_at).toLocaleString()}
                      {a.status !== "pending" && (
                        <span className="ml-2">
                          · {a.status === "approved" ? "✅" : "❌"} {a.status}
                          {a.resolved_by_name ? ` by ${a.resolved_by_name}` : ""}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                  {a.status === "pending" && d.event && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {outlook && (
                        <button data-testid={`approve-outlook-btn-${a.id}`} disabled={busy === a.id}
                                onClick={() => toOutlook(a)}
                                className="btn-solid order-3 inline-flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm disabled:opacity-50">
                          <CalendarPlus size={15} /> Add to Outlook
                        </button>
                      )}
                      <button data-testid={`approve-btn-${a.id}`} disabled={busy === a.id}
                              onClick={() => resolve(a.id, "approve", { add_to_outlook: false })}
                              className={`${outlook ? "btn-soft" : "btn-solid"} order-2 inline-flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm disabled:opacity-50`}>
                        <CheckCircle2 size={15} /> {outlook ? "Keep in Yoda only" : "Yes"}
                      </button>
                      <button data-testid={`deny-btn-${a.id}`} disabled={busy === a.id}
                              onClick={() => resolve(a.id, "deny")}
                              className="btn-soft order-1 inline-flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm disabled:opacity-50">
                        <XCircle size={15} /> No, take it off
                      </button>
                    </div>
                  )}
                  {a.status === "pending" && d.rename && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(!d.outlookEvent || outlook) && (
                        <button data-testid={`approve-outlook-btn-${a.id}`} disabled={busy === a.id}
                                onClick={() => toOutlook(a)}
                                className="btn-solid order-3 inline-flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm disabled:opacity-50">
                          <CalendarPlus size={15} /> {d.outlookEvent ? "Update Outlook" : "Rename for everyone"}
                        </button>
                      )}
                      <button data-testid={`approve-btn-${a.id}`} disabled={busy === a.id}
                              onClick={() => resolve(a.id, "approve", { add_to_outlook: false })}
                              className="btn-soft order-2 inline-flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm disabled:opacity-50">
                        <CheckCircle2 size={15} /> Just for {d.first}
                      </button>
                      <button data-testid={`deny-btn-${a.id}`} disabled={busy === a.id}
                              onClick={() => resolve(a.id, "deny")}
                              className="btn-soft order-1 inline-flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm disabled:opacity-50">
                        <XCircle size={15} /> No, keep the old name
                      </button>
                    </div>
                  )}
                  {a.status === "pending" && !d.event && !d.rename && (
                    <div className="mt-3 flex gap-2">
                      <button data-testid={`approve-btn-${a.id}`}
                              disabled={busy === a.id}
                              onClick={() => resolve(a.id, "approve")}
                              className="btn-solid order-2 inline-flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm disabled:opacity-50">
                        <CheckCircle2 size={15} /> Yes
                      </button>
                      <button data-testid={`deny-btn-${a.id}`}
                              disabled={busy === a.id}
                              onClick={() => resolve(a.id, "deny")}
                              className="btn-soft order-1 inline-flex flex-1 items-center justify-center gap-1.5 py-2.5 text-sm disabled:opacity-50">
                        <XCircle size={15} /> No
                      </button>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
